import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiExternalLink } from 'react-icons/fi';
import axios from 'axios';

interface Project {
  id: string;
  title: string;
  year: number;
  description: string;
  image: string;
  techStack: string[];
  features: string[];
  businessValue: string[];
  technicalAchievements: string[];
  useCases: string[];
  url: string | null;
}

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`/api/projects/${id}`);
        setProject(response.data);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading project details...
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail">
        <Link to="/" className="back-button">
          <FiArrowLeft />
          Back to Projects
        </Link>
        <div className="detail-container">
          <div className="detail-hero">
            <h1 className="detail-title">Project Not Found</h1>
            <p className="detail-description">
              The project you're looking for doesn't exist or may have been moved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-detail">
      <Link to="/" className="back-button">
        <FiArrowLeft />
        Back to Projects
      </Link>
      
      <div className="detail-container">
        <motion.div
          className="detail-hero"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <img 
            src={project.image} 
            alt={project.title}
            className="detail-image"
          />
          <h1 className="detail-title">{project.title}</h1>
          <div className="project-year" style={{ 
            fontSize: '1.2rem', 
            fontWeight: '600', 
            color: '#ffffff', 
            marginBottom: '1rem',
            borderBottom: '2px solid rgba(255, 255, 255, 0.3)',
            paddingBottom: '0.5rem',
            opacity: 0.9
          }}>
            {project.year}
          </div>
          <p className="detail-description">{project.description}</p>
          
          {project.url && (
            <motion.a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
              style={{ fontSize: '1.1rem', marginTop: '1rem' }}
              whileHover={{ scale: 1.05 }}
            >
              <FiExternalLink />
              View Live Project
            </motion.a>
          )}
        </motion.div>

        <motion.div
          className="detail-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="features-section">
            <h2 className="section-title">Key Features</h2>
            <ul className="features-list">
              {project.features.map((feature, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  {feature}
                </motion.li>
              ))}
            </ul>
          </div>

          <div className="tech-section">
            <h2 className="section-title">Technology Stack</h2>
            <div className="tech-grid">
              {project.techStack.map((tech, index) => (
                <motion.div
                  key={index}
                  className="tech-item"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  whileHover={{ scale: 1.05 }}
                >
                  {tech}
                </motion.div>
              ))}
            </div>
          </div>

          <div className="features-section">
            <h2 className="section-title">Business Value</h2>
            <ul className="features-list">
              {project.businessValue.map((value, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  {value}
                </motion.li>
              ))}
            </ul>
          </div>

          <div className="features-section">
            <h2 className="section-title">Technical Achievements</h2>
            <ul className="features-list">
              {project.technicalAchievements.map((achievement, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  {achievement}
                </motion.li>
              ))}
            </ul>
          </div>

          <div className="features-section">
            <h2 className="section-title">Use Cases</h2>
            <ul className="features-list">
              {project.useCases.map((useCase, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                >
                  {useCase}
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetail; 